import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: "website" | "article";
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
  };
}

const BASE_URL = "https://getsent.dev";
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;
const SITE_NAME = "GetSent";

export function SEO({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  article,
}: SEOProps) {
  useEffect(() => {
    // Set document title
    const fullTitle = title === "GetSent" ? title : `${title} | ${SITE_NAME}`;
    document.title = fullTitle;

    // Helper to set meta tag
    const setMeta = (property: string, content: string, isName = false) => {
      const attr = isName ? "name" : "property";
      let element = document.querySelector(`meta[${attr}="${property}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attr, property);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    // Helper to set link tag
    const setLink = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement("link");
        element.setAttribute("rel", rel);
        document.head.appendChild(element);
      }
      element.setAttribute("href", href);
    };

    // Basic meta tags
    setMeta("description", description, true);

    // Canonical URL
    const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : BASE_URL;
    setLink("canonical", canonicalUrl);

    // Open Graph tags
    setMeta("og:title", fullTitle);
    setMeta("og:description", description);
    setMeta("og:url", canonicalUrl);
    setMeta("og:image", ogImage.startsWith("http") ? ogImage : `${BASE_URL}${ogImage}`);
    setMeta("og:type", ogType);
    setMeta("og:site_name", SITE_NAME);

    // Twitter tags
    setMeta("twitter:card", "summary_large_image", true);
    setMeta("twitter:title", fullTitle, true);
    setMeta("twitter:description", description, true);
    setMeta("twitter:image", ogImage.startsWith("http") ? ogImage : `${BASE_URL}${ogImage}`, true);

    // Article-specific meta tags
    if (ogType === "article" && article) {
      if (article.publishedTime) {
        setMeta("article:published_time", article.publishedTime);
      }
      if (article.modifiedTime) {
        setMeta("article:modified_time", article.modifiedTime);
      }
      if (article.author) {
        setMeta("article:author", article.author);
      }
    }

    // Cleanup function to reset to defaults when component unmounts
    return () => {
      document.title = `${SITE_NAME} - DMARC Monitoring & Email Authentication`;
    };
  }, [title, description, canonical, ogImage, ogType, article]);

  return null;
}
